var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import React from 'react';
import { formatPhone } from '@rad/utility/formatting';
import { Instant } from '../../utility/datetime';
import { getJson, postObjectJson } from '../../utility/request';
// Returns null when the client has no phone number on file (cannot SMS).
// Prefers a mobile contact, otherwise falls back to the first phone listed.
export const clientToConversationSeed = (client) => {
    const phones = client.contacts.filter((x) => x.kind === 'phone');
    if (phones.length === 0) {
        return null;
    }
    const phone = (phones.find((p) => p.subkind === 'mobile') || phones[0]).value;
    const name = `${client.firstname} ${client.lastname}`.trim() || '(unnamed)';
    const initials = `${(client.firstname || '').charAt(0)}${(client.lastname || '').charAt(0)}`.toUpperCase() || '?';
    return {
        'customer_id': client.id,
        'customer_name': name,
        'customer_phone': formatPhone(phone),
        'customer_initials': initials,
    };
};
const CONV_REFRESHED_EVENT = 'crm-conversations-refreshed';
const POLL_INTERVAL_MS = 15000;
const digitsOnly = (s) => (s || '').replace(/\D/g, '');
const notifyRefresh = () => {
    window.dispatchEvent(new CustomEvent(CONV_REFRESHED_EVENT));
};
const draftStore = (merchantId) => {
    const w = window;
    return {
        'get': () => { var _a; return ((_a = w.__crmDrafts) === null || _a === void 0 ? void 0 : _a[merchantId]) || {}; },
        'set': (d) => { w.__crmDrafts = Object.assign(Object.assign({}, w.__crmDrafts), { [merchantId]: d }); notifyRefresh(); },
    };
};
const fetchConversations = (merchantId) => __awaiter(void 0, void 0, void 0, function* () {
    const rows = yield getJson(`/merchants/${merchantId}/crm/conversations`);
    return rows.map((r) => (Object.assign(Object.assign({}, r), { 'is_draft': false })));
});
// Latest server-side snapshot, updated by useConversations. Read by
// findOrCreateConversation so it can dedupe against existing real
// threads before creating a draft.
const latestServerConversations = {};
export const useConversations = (merchantId) => {
    const [items, setItems] = React.useState(() => ({ merchantId, 'conversations': latestServerConversations[merchantId] || [] }));
    const [drafts, setDraftsState] = React.useState(() => draftStore(merchantId).get());
    React.useEffect(() => {
        let cancelled = false;
        let timer = null;
        const tick = () => __awaiter(void 0, void 0, void 0, function* () {
            if (timer) {
                clearTimeout(timer);
                timer = null;
            }
            try {
                const next = yield fetchConversations(merchantId);
                if (!cancelled) {
                    latestServerConversations[merchantId] = next;
                    setItems({ merchantId, 'conversations': next });
                }
            }
            catch ( /* ignore transient errors */_a) { /* ignore transient errors */ }
            if (!cancelled) {
                timer = setTimeout(tick, POLL_INTERVAL_MS);
            }
        });
        setDraftsState(draftStore(merchantId).get());
        tick();
        const onRefresh = () => {
            setDraftsState(draftStore(merchantId).get());
            tick();
        };
        window.addEventListener(CONV_REFRESHED_EVENT, onRefresh);
        return () => {
            cancelled = true;
            if (timer) {
                clearTimeout(timer);
            }
            window.removeEventListener(CONV_REFRESHED_EVENT, onRefresh);
        };
    }, [merchantId]);
    return React.useMemo(() => {
        const conversations = items.merchantId === merchantId ? items.conversations : latestServerConversations[merchantId] || [];
        const realPhones = new Set(conversations.map((c) => digitsOnly(c.customer_phone)));
        const draftItems = Object.values(items.merchantId === merchantId ? drafts : draftStore(merchantId).get()).filter((d) => !realPhones.has(digitsOnly(d.customer_phone)));
        return [...conversations, ...draftItems].sort((a, b) => b.last_message_at.localeCompare(a.last_message_at));
    }, [merchantId, items, drafts]);
};
export const useUnreadCount = (merchantId) => {
    const cs = useConversations(merchantId);
    // Archived threads don't contribute to the header badge — moving a thread
    // to Archived is how a merchant says "I'm done with this."
    return cs.reduce((agg, x) => { var _a; return agg + (((_a = x.status) !== null && _a !== void 0 ? _a : 'open') === 'open' ? x.unread_count : 0); }, 0);
};
// Look for an existing conversation matching this seed (by customer_id
// or normalized phone). If none, create a client-side draft; the draft
// is materialized server-side on first sendMessage() call.
export const findOrCreateConversation = (merchantId, seed) => {
    const seedDigits = digitsOnly(seed.customer_phone);
    const match = (latestServerConversations[merchantId] || []).find((c) => (seed.customer_id !== null && c.customer_id === seed.customer_id)
        || digitsOnly(c.customer_phone) === seedDigits);
    if (match) {
        return match.id;
    }
    const store = draftStore(merchantId);
    const drafts = store.get();
    const existingDraft = Object.values(drafts).find((d) => digitsOnly(d.customer_phone) === seedDigits);
    if (existingDraft) {
        return existingDraft.id;
    }
    const id = `draft:${seedDigits}:${Instant.now().toEpochMilli().toString(36)}`;
    const draft = {
        'id': id,
        'customer_id': seed.customer_id,
        'customer_name': seed.customer_name,
        'customer_phone': seed.customer_phone,
        'customer_initials': seed.customer_initials,
        'last_message_preview': '(no messages yet)',
        'last_message_at': Instant.now().toString(),
        'unread_count': 0,
        'status': 'open',
        'messages': [],
        'is_draft': true,
    };
    store.set(Object.assign(Object.assign({}, drafts), { [id]: draft }));
    return id;
};
const toE164 = (phone) => {
    const digits = digitsOnly(phone);
    if (digits.length === 10) {
        return `+1${digits}`;
    }
    if (digits.length === 11 && digits.startsWith('1')) {
        return `+${digits}`;
    }
    return `+${digits}`;
};
// Materialize a draft server-side via /crm/sms/send. Returns the real
// backend conversation_id so callers (conversations.tsx) can swap their
// activeId over.
const sendDraftMessage = (merchantId, draftId, body) => __awaiter(void 0, void 0, void 0, function* () {
    const store = draftStore(merchantId);
    const drafts = store.get();
    const draft = drafts[draftId];
    if (!draft) {
        return null;
    }
    const response = yield postObjectJson(`/merchants/${merchantId}/crm/sms/send`, { 'phone': toE164(draft.customer_phone), 'message': body, 'name': draft.customer_name });
    const remaining = Object.assign({}, drafts);
    delete remaining[draftId];
    store.set(remaining);
    return response.conversation_id;
});
// Send an outbound message on an existing thread. Returns the resolved
// backend conversation id (equal to input, unless it was a draft that
// just got materialized).
export const sendMessage = (merchantId, conversationId, body) => __awaiter(void 0, void 0, void 0, function* () {
    if (conversationId.startsWith('draft:')) {
        const realId = yield sendDraftMessage(merchantId, conversationId, body);
        notifyRefresh();
        return realId;
    }
    yield postObjectJson(`/merchants/${merchantId}/crm/conversations/${conversationId}/messages`, { 'message': body });
    notifyRefresh();
    return conversationId;
});
export const markRead = (merchantId, conversationId) => __awaiter(void 0, void 0, void 0, function* () {
    if (conversationId.startsWith('draft:')) {
        return;
    }
    try {
        yield postObjectJson(`/merchants/${merchantId}/crm/conversations/${conversationId}/read`, {});
        notifyRefresh();
    }
    catch ( /* ignore */_a) { /* ignore */ }
});
export const setArchived = (merchantId, conversationId, archived) => __awaiter(void 0, void 0, void 0, function* () {
    if (conversationId.startsWith('draft:')) {
        return;
    }
    const action = archived ? 'archive' : 'unarchive';
    try {
        yield postObjectJson(`/merchants/${merchantId}/crm/conversations/${conversationId}/${action}`, {});
        notifyRefresh();
    }
    catch ( /* refresh will re-sync from server state */_a) { /* refresh will re-sync from server state */ }
});
// Bind a newly-created RR client to an existing conversation. Used by
// the admin "Add Customer" flow when the user resolves an unmatched
// thread. Server-side, this overwrites any prior client_id guess because
// the merchant is telling us who the counterparty actually is; the
// optional display fields also refresh the sidebar snapshot from the raw
// phone to the client's real name.
export const linkConversationToCustomer = (merchantId, conversationId, customerId, displayName, displayInitials) => __awaiter(void 0, void 0, void 0, function* () {
    if (conversationId.startsWith('draft:')) {
        return;
    }
    try {
        yield postObjectJson(`/merchants/${merchantId}/crm/conversations/${conversationId}/link-client`, Object.assign(Object.assign({ 'client_id': customerId }, (displayName ? { 'display_name': displayName } : {})), (displayInitials ? { 'display_initials': displayInitials } : {})));
        notifyRefresh();
    }
    catch ( /* refresh will re-sync from server state */_a) { /* refresh will re-sync from server state */ }
});
